import { useMemo } from "react";
import { useAccount } from "wagmi";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import type { ChainType } from "~/api/types";

export interface UnifiedWallet {
  address: string | null;
  chainType: ChainType | null;
  chainId: string | null;
  isConnected: boolean;
  disconnect: () => void;
}

/**
 * Unified wallet hook that works across EVM, Solana, and Tron wallets.
 * Uses Dynamic Labs as the primary wallet source, with wagmi as EVM fallback.
 */
export function useUnifiedWallet(): UnifiedWallet {
  const { primaryWallet, handleLogOut } = useDynamicContext();
  const evmAccount = useAccount();

  return useMemo(() => {
    // If Dynamic has a primary wallet, use it
    if (primaryWallet) {
      const network = primaryWallet.chain;
      let chainType: ChainType;
      let chainId: string | null = null;

      if (network === "SOL") {
        chainType = "solana";
        chainId = "solana";
      } else if (network === "TRON") {
        chainType = "tron";
        chainId = "tron";
      } else {
        // EVM: get chain ID from wagmi or Dynamic
        chainType = "evm";
        chainId = evmAccount.chainId ? String(evmAccount.chainId) : null;
      }

      return {
        address: primaryWallet.address,
        chainType,
        chainId,
        isConnected: true,
        disconnect: () => handleLogOut(),
      };
    }

    // Fallback to wagmi for pure EVM connections
    if (evmAccount.isConnected && evmAccount.address) {
      return {
        address: evmAccount.address,
        chainType: "evm",
        chainId: evmAccount.chainId ? String(evmAccount.chainId) : null,
        isConnected: true,
        disconnect: () => handleLogOut(),
      };
    }

    return {
      address: null,
      chainType: null,
      chainId: null,
      isConnected: false,
      disconnect: () => {},
    };
  }, [primaryWallet, evmAccount.isConnected, evmAccount.address, evmAccount.chainId, handleLogOut]);
}
